// Driver is a feature that allows on-screen elements to drive
// changes to appearance. A simple example is a draggable circle
// that can be dragged to change the width and height of a rectangle.
/*
    Notes on Snapping

    Snapping is only supported on select few snap types. Snapping ignores
    bounds so best to use it on driver types that have INFINITE_BOUNDS.
*/
import { g } from "analytics";
import { clamp, P } from "apparatus/library/common";
import { UpdateAppearance } from "command/basic";
import { Sequence } from "command/command";
import { createInteractionHandleFor } from "editing/handle";
import { Adjustable } from "editing/interaction";
import { transform } from "editing/snapping/type";
import { Key, Rectangle } from "paper";
export const INFINITE_BOUNDS = { minX: -10000, maxX: 10000, minY: -10000, maxY: 10000 };
function restrainRectangleDriver(position, bounds) {
    const x = Math.round(clamp(position.x, bounds.left, bounds.right));
    const y = Math.round(clamp(position.y, bounds.top, bounds.bottom));
    return P(x, y);
}
/** Computes the delta in the component's coordinate system, from a screen-space delta. */
function adjustDriverDelta(rawDelta, driver, component) {
    const rotation = -component.currentRotation;
    const flipped = component.props.flipped;
    return rawDelta.rotate(rotation).multiply([flipped ? -1.0 : 1.0, 1.0]).multiply(toPoint(driver.scale));
}
/** Converts a number or Point to a Point */
function toPoint(i) {
    return typeof (i) === "number" ? P(i, i) : i;
}
/** Applies the constraint, if present. */
function maybeApplyConstraint(position, driver, component) {
    if (!driver.constraints)
        return position;
    // Workaround for return type widening.
    let partialAppearance = {};
    driver.toAppearance((a) => { partialAppearance = a; }, position, component.props.appearance);
    const maybeNewAppearance = driver.constraints(Object.assign(Object.assign({}, component.props.appearance), partialAppearance), Key.modifiers);
    if (!maybeNewAppearance)
        return position;
    return driver.fromAppearance(maybeNewAppearance);
}
export function createRectangleDriver(driver, component) {
    let currentPosition = driver.fromAppearance(component.props.appearance);
    let handle;
    /** Restraint the driver to this area. Pre-calculated onStart. */
    let currentRectangleRestraints;
    handle = createInteractionHandleFor(Adjustable({
        onStart: () => {
            const appearance = component.props.appearance;
            // Calculate restraining rectangle
            currentRectangleRestraints = new Rectangle(0, 0, 0, 0);
            currentRectangleRestraints.left = (typeof driver.minX === "number") ? driver.minX : driver.minX(appearance);
            currentRectangleRestraints.right = (typeof driver.maxX === "number") ? driver.maxX : driver.maxX(appearance);
            currentRectangleRestraints.top = (typeof driver.minY === "number") ? driver.minY : driver.minY(appearance);
            currentRectangleRestraints.bottom = (typeof driver.maxY === "number") ? driver.maxY : driver.maxY(appearance);
            if (!driver.snapping)
                return {};
            // Driver snapping has to be evaluated every time in case
            // component appearance has changed.
            const snapping = driver.snapping(appearance, currentPosition);
            if (!snapping)
                return {};
            return { snappableComponent: new AdjustableSnappableComponent(component, snapping) };
        },
        setDelta: delta => {
            // Compute new position and then apply the appearance changes.
            delta = adjustDriverDelta(delta, driver, component);
            let restrainedPosition = restrainRectangleDriver(currentPosition.add(delta), currentRectangleRestraints);
            const finalPosition = maybeApplyConstraint(restrainedPosition, driver, component);
            handle.position = finalPosition;
            driver.toAppearance(a => component.updateMultipleAppearanceAttributes(a), finalPosition, component.props.appearance);
        },
        onEnd: delta => {
            g("UpdateAppearance", `${component.apparatusType}::{driver}`);
            delta = adjustDriverDelta(delta, driver, component);
            let restrainedPosition = restrainRectangleDriver(currentPosition.add(delta), currentRectangleRestraints);
            const finalPosition = maybeApplyConstraint(restrainedPosition, driver, component);
            let newAppearance = {};
            driver.toAppearance((a) => { newAppearance = a; }, finalPosition, component.props.appearance);
            const commands = [];
            for (const key in newAppearance) {
                commands.push(new UpdateAppearance(component.id, key, newAppearance[key], component.props.appearance[key]));
            }
            return new Sequence(commands);
        },
    }));
    return {
        handle: handle,
        onPropsChanged: (newAppearance) => {
            currentPosition = driver.fromAppearance(newAppearance);
            handle.position = currentPosition;
            handle.visible = driver.visible ? driver.visible(newAppearance) : true;
        },
        onStateChanged: (newAppearance) => {
            handle.position = driver.fromAppearance(newAppearance);
        }
    };
}
/** Implements the SnappableComponent for drivers. */
class AdjustableSnappableComponent {
    constructor(component, snap) {
        this.component = component;
        this.globalSnapping = [
            transform(snap, component.props.flipped, p => component.localToGlobal(p))
        ];
    }
    // Proxy remaining methods.
    get rotation() { return this.component.rotation; }
    get id() { return this.component.id; }
    get isSelected() { return this.component.isSelected; }
    setSnapVisualisation(filter) {
        this.component.setSnapVisualisation(filter);
    }
}
